import { AlertTriangle, Clock } from "lucide-react";
import { Link } from "react-router-dom";

import type { SubscriptionState } from "@/lib/types";
import { useFormat, useT } from "@/lib/i18n";

/** Faixa de estado da assinatura, no topo de toda tela do painel.
 *
 * Existe porque descobrir que a conta está travada **no meio de uma
 * tarefa** é o pior jeito de descobrir: o dono clica em "salvar" com o
 * cliente no balcão e recebe uma negativa que parece defeito do sistema.
 * A faixa transforma isso em informação que ele já tinha antes de tentar.
 *
 * Some sozinha quando não há o que dizer — assinatura ativa não ganha
 * enfeite, senão vira paisagem e ninguém lê quando importar.
 */
export default function SubscriptionBanner({
  subscription,
}: {
  subscription: SubscriptionState | null;
}) {
  const t = useT();
  const { locale } = useFormat();
  if (!subscription) return null;

  if (subscription.is_readonly) {
    // A data da carência é lida por quem está prestes a perder a agenda —
    // no formato do país dele. "03/04" não é o mesmo dia nos dois.
    const ate = subscription.grace_ends_at
      ? new Date(subscription.grace_ends_at).toLocaleDateString(locale)
      : null;

    return (
      <Faixa tom="alerta" icone={<AlertTriangle size={16} />}>
        <strong className="font-semibold">{t("banner.overdue")}</strong>{" "}
        {t("banner.overdueText", { until: ate ? t("banner.until", { date: ate }) : "" })}{" "}
        <Link className="underline underline-offset-2" to="/assinatura">
          {t("banner.fixNow")}
        </Link>
      </Faixa>
    );
  }

  // Só na última semana: avisar no primeiro dia de um teste de 30 é ruído,
  // e ruído diário treina a pessoa a não ler a faixa.
  if (subscription.status === "trialing" && subscription.trial_days_left <= 7) {
    const dias = subscription.trial_days_left;
    return (
      <Faixa tom="aviso" icone={<Clock size={16} />}>
        {dias === 0 ? (
          <>
            <strong className="font-semibold">{t("modal.trialEndsToday")}</strong>{" "}
            {t("banner.readOnlyAfter")}
          </>
        ) : (
          <>
            <strong className="font-semibold">{t("banner.daysLeft", { n: dias })}</strong>{" "}
            {t("banner.ofYourTrial", { plan: subscription.plan.name })}
          </>
        )}{" "}
        <Link className="underline underline-offset-2" to="/assinatura">
          {t("banner.choosePlan")}
        </Link>
      </Faixa>
    );
  }

  return null;
}

function Faixa({
  tom,
  icone,
  children,
}: {
  tom: "alerta" | "aviso";
  icone: React.ReactNode;
  children: React.ReactNode;
}) {
  const cores =
    tom === "alerta"
      ? "border-red-900/60 bg-red-950/40 text-red-200"
      : "border-amber-800/50 bg-amber-950/30 text-amber-100";

  return (
    <div className={`flex items-start gap-2.5 border-b px-4 py-3 text-sm lg:px-8 ${cores}`}>
      <span className="mt-0.5 shrink-0">{icone}</span>
      <p className="min-w-0">{children}</p>
    </div>
  );
}
